import type { ReactNode } from "react";
import { AnimatedNumber, Typography } from "../../../../../../../shared/frontend/ui";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly, formatDateRange, formatMoney, formatNumber, formatWalletBalance, parseDateOnly } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { WALLET_TYPE_POINTS } from "../../../app/constants/walletType";
import { OverviewKpi } from "./overview/OverviewKpi";
import type { PersonOverviewDto, PersonOverviewPackage } from "./usePersonOverview";
import "./personOverview.css";

const PERCENT_SCALE = 100;

type Props = {
  overview: PersonOverviewDto | undefined;
  refreshing?: boolean;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  /** Zutrittsmedien als Kennzahl (Anzahl, vor dem Laden `null`), die Liste darunter nur, wenn es Medien gibt. */
  accessMedia?: { count: number | null; list: ReactNode };
};

/** Vergangener Anteil der Laufzeit in Prozent; ohne Enddatum `undefined` (unbefristet, kein Balken). */
function elapsedPercent(item: PersonOverviewPackage, today = new Date()): number | undefined {
  const start = parseDateOnly(item.startDate);
  const end = parseDateOnly(item.effectiveEndDate ?? item.cancellationEffectiveDate);
  if (!start || !end || end.getTime() <= start.getTime()) return undefined;
  const share = (today.getTime() - start.getTime()) / (end.getTime() - start.getTime());
  return Math.round(Math.max(0, Math.min(1, share)) * PERCENT_SCALE);
}

/**
 * Inhalt des Blocks „Mitgliedschaft“ der Personenübersicht, in der Schriftskala des Lagebilds: oben je laufendem
 * Vertragspaket eine Zeile über die volle Breite (Paketname, Ende bzw. Kündigung, Laufzeitbalken, Ruhezeit),
 * darunter eine Kennzahlzeile mit Wertekonten, Punkten, Guthaben und Zutrittsmedien.
 */
export function PersonOverviewMembershipBody({ overview, refreshing, text, units, accessMedia }: Props) {
  const contracts = overview?.contracts;
  const finance = overview?.finance;
  return (
    <div className="person-overview-membership">
      {contracts && contracts.packages.length === 0 ? (
        <Typography.Text type="secondary">{text("Person overview no running contract")}</Typography.Text>
      ) : null}
      {contracts?.packages.map((item) => {
        const percent = elapsedPercent(item);
        const cancelled = Boolean(item.cancellationEffectiveDate);
        const end = item.effectiveEndDate ?? item.cancellationEffectiveDate;
        return (
          <OverviewKpi
            key={item.id}
            label={item.name || "—"}
            tone={cancelled ? "warn" : "neutral"}
            progress={percent}
            progressLabel={percent != null ? text("Person overview term progress").replace("{0}", formatNumber(percent, units)) : undefined}
            sub={item.restPeriod
              ? text("Person overview rest period range").replace("{0}", formatDateRange(item.restPeriod.startDate, item.restPeriod.endDate, units))
              : cancelled ? text("Cancelled") : undefined}
          >
            {end ? formatDateOnly(end, units) : text("Person overview renews")}
          </OverviewKpi>
        );
      })}
      <div className="person-overview-kpis person-overview-kpis--start">
        {finance ? (
          <>
            <OverviewKpi label={text("Wallets")} refreshing={refreshing}>{formatNumber(finance.walletCount, units)}</OverviewKpi>
            {finance.pointsBalance !== 0 ? (
              <OverviewKpi label={text("Person overview points")} refreshing={refreshing}>
                <AnimatedNumber value={finance.pointsBalance} format={(value) => formatWalletBalance(Math.round(value), WALLET_TYPE_POINTS, units)} />
              </OverviewKpi>
            ) : null}
            {finance.currencyBalance !== 0 ? (
              <OverviewKpi label={text("Person overview credit")} refreshing={refreshing}>
                <AnimatedNumber value={finance.currencyBalance} format={(value) => formatMoney(value, units)} />
              </OverviewKpi>
            ) : null}
          </>
        ) : null}
        {accessMedia ? (
          <OverviewKpi label={text("Access media")}>{accessMedia.count == null ? "–" : formatNumber(accessMedia.count, units)}</OverviewKpi>
        ) : null}
      </div>
      {accessMedia?.list}
    </div>
  );
}
